import {updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {PipelineGraphQL, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {isDataFrameView, resolveLineageStep, resolveOutputStepData} from 'pipelines/PipelineViewUtils';
import * as logging from 'util/Logging';
import {withLoadingScreen} from 'util/Promises';

const LOADING_MESSAGE = 'Renaming view...';

/**
 * Step types that can be renamed directly, by editing their config.
 *
 * If the terminal step in a view is one of these step types, the view can be renamed by updating the step.
 *
 * Otherwise, a TABLE_VIEW step must be added to the view.
 */
const RENAMEABLE_STEP_TYPES = [
  PipelineStepType.CHART_VIEW,
  PipelineStepType.REGRESSION,
  PipelineStepType.TABLE_VIEW,
];

/**
 * Check if a view's title can be edited.
 *
 * A view's title can be edited if the view ends in a CHART_VIEW or REGRESSION step, or if the view is a
 * data frame view.
 */
export function canEditTitle(analysis: PipelineGraphQL, analysisView: PipelineViewGraphQL): boolean {
  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const lastStep = resolveLineageStep(analysis.steps, lastNode);
  return (
    !isAnalysisReadOnly(analysis) &&
    lastStep.statusCode === PipelineStepStatusCode.SUCCEEDED &&
    (RENAMEABLE_STEP_TYPES.includes(lastStep.stepType) || isDataFrameView(analysisView, analysis.steps))
  );
}

export default async function handleOnTitleChange(
  newTitle: string,
  analysis: PipelineGraphQL,
  analysisView: PipelineViewGraphQL
): Promise<void> {
  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const lastStep = resolveLineageStep(analysis.steps, lastNode);

  if (!canEditTitle(analysis, analysisView) || lastNode.stepOutputReference == null) {
    logging.error('Tried to rename a view that cannot be renamed.');
    return;
  }

  if (RENAMEABLE_STEP_TYPES.includes(lastStep.stepType)) {
    await withLoadingScreen(
      updateAndRunAnalysisStep(analysis.id, {
        stepId: lastNode.step.id,
        stepConfig: getNewConfigForStep(lastStep, newTitle),
      }),
      LOADING_MESSAGE
    );
  } else {
    const {stepOutputReference} = lastNode;
    const outputStepData = resolveOutputStepData(analysis.steps, {
      step: lastNode.step,
      stepOutputReference,
    });
    await withLoadingScreen(
      addStepWithInferredEdges(analysis, {
        stepType: PipelineStepType.TABLE_VIEW,
        stepData: outputStepData,
        stepDataIndex: stepOutputReference.rawDataIndex,
        config: {viewName: newTitle},
        runAfterCreate: true,
      }),
      LOADING_MESSAGE
    );
  }
}

function getNewConfigForStep(step: PipelineStep, newTitle: string): Record<string, unknown> {
  switch (step.stepType) {
    case PipelineStepType.CHART_VIEW:
      return {
        ...step.config,
        chartName: newTitle,
      };
    case PipelineStepType.REGRESSION:
      return {
        ...step.config,
        name: newTitle,
      };
    case PipelineStepType.TABLE_VIEW:
      return {
        ...step.config,
        viewName: newTitle,
      };
    default:
      throw Error(`Incompatible step type ${step.stepType}`);
  }
}
